import React, { useContext, useEffect, useRef, useState } from 'react';
import SvgPencil from '@icons/pencil-inverted.svg?react';
import { resolveStateIcon } from '@utils/reservation.ts';
import { EntityTypeEnum, State, WorkflowTransitionOutDTO } from '@api/ringil-api.v2.tsx';
import { ApiContext } from '@api/api.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';

type WorkflowStateButtonProps = {
  state?: State;
  entityId: string;
  entityType: EntityTypeEnum;
  translationKeyPrefix: string;
  disabled?: boolean;
  onStateChange: (stateId: string) => void;

};

export const WorkflowStateButton: React.FC<WorkflowStateButtonProps> = ({
                                                                          state,
                                                                          entityId,
                                                                          entityType,
                                                                          translationKeyPrefix,
                                                                          disabled = false,
                                                                          onStateChange,
                                                                        }) => {
  const { tr } = useTranslationLgs();
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { getWorkflowPossibleTransition, performTransitionOnEntity } = useContext(ApiContext);
  const [possibleTransition, setPossibleTransition] = useState<WorkflowTransitionOutDTO[]>([]);

  useEffect(() => {
    // The possible-transitions EP requires change permission; skip it when the button is read-only
    // (e.g. a partner who may see but not change status) so it does not needlessly 403.
    if (disabled) return;
    getWorkflowPossibleTransition(entityId, entityType, d => {
      setPossibleTransition(Array.isArray(d?.possibleTransitions) ? d.possibleTransitions.filter(transition => transition.to.name !== state.name) : []);
    });
  }, [state, disabled]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setActive(false);
      }
    }

    if (active) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [active]);

  const handleButtonClick = () => {
    if (!disabled) setActive((prev) => !prev);
  };

  const handleStateClick = async (stateId: string) => {
    setLoading(true);
    onStateChange(stateId);
    setLoading(false);
    setActive(false);
  };

  if (!state) return null;

  return (
    <div className="relative overflow-visible" ref={wrapperRef}>
      <div
        onClick={!disabled ? handleButtonClick : undefined}
        className={`flex px-2 py-1 justify-between items-center rounded border border-solid w-auto
          transition-colors
          ${
            disabled
              ? 'border border-gray-300 bg-white cursor-not-allowed'
              : active
                ? 'border border-content-brand bg-content-brandMedium'
                : 'border border-content-brand bg-base-secondary hover:bg-content-brandMedium'
          }
        `}
      >
        <div className="flex items-center">
          {state.icon && <span>{resolveStateIcon(state.icon)}</span>}
          <span className="text-[14px] font-normal leading-none text-content-primary font-dm-sans">
            {tr(`${translationKeyPrefix}${state.name}`, state.name)}
          </span>
        </div>
        {!disabled && <SvgPencil className={`text-content-brand ml-2 mr-1 shrink-0 fill-current`} />}
      </div>
      {active && !disabled && (
        <div
          className="absolute left-0 top-full border rounded-lg shadow z-10 flex flex-col justify-end items-start"
          style={{
            background: 'var(--Base-BaseSecondary, #FFF)',
            boxShadow: '0 8px 24px 0 rgba(0, 0, 0, 0.20)',
          }}
        >
          {possibleTransition.map((state, idx) => (
            <div
              key={idx}
              className={`flex w-[297px] px-4 py-3 items-center gap-4 text-left hover:bg-base-quaternary
                ${idx < possibleTransition.length - 1 ? 'border-b border-gray-200' : ''}
                cursor-pointer
              `}
              onClick={() => !loading && handleStateClick(state.to.name)}
            >
              {state.to.icon && <span>{resolveStateIcon(state.to.icon)}</span>}
              {tr(`${translationKeyPrefix}${state.to.name}`, state.to.name)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};